<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import capitalize from 'lodash/capitalize';
import { getCurrentLocale } from '@/i18n';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useViewRuleStore } from '@/store/ViewRuleStore';
import { LANG_FRENCH } from '@/constants';
import { getTextVariant } from '@/utils/stringUtils';
import { useViewComposable } from '@/views/composable';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import TableColumn from 'primevue/column';
import HTMLDisplay from '@/components/common/HTMLDisplay.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ManageRuleAssignmentModal from '@/components/admin/RuleAssignment/ManageRuleAssignmentModal.vue';

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const dialog = useDialog();
const viewRuleStore = useViewRuleStore();
const { departments } = useViewComposable();

const ruleAssignment = ref<any>([]);
const filters = ref();
const isLoading = ref(false);
const globalFilterFields = ref(['section', 'rule', 'ruleFr', 'ruleOutcomeTitle', 'ruleOutcomeTitleFr', 'priority']);

const convertKey = () => {
  return getTextVariant(currentLanguage, '', capitalize(LANG_FRENCH));
};

const columns = ref([
  { field: 'section', header: t('rule_assignment.rule_section') },
  { field: `rule${convertKey()}`, header: t('rule_assignment.rule') },
  { field: `ruleOutcomeTitle${convertKey()}`, header: t('rule_assignment.rule_outcome') },
  { field: 'autoAssign', header: t('rule_assignment.auto_assign') },
  { field: 'priority', header: t('rule_assignment.priority') },
  { field: 'ruleAbbreviation', header: t('common.action') }
]);

const initFilters = () => {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    section: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`rule${convertKey()}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    priority: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    [`ruleOutcomeTitle${convertKey()}`]: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const clearFilter = () => {
  initFilters();
};

const convertDataRuleOutcome = (data: any) => {
  return getTextVariant(currentLanguage, data.ruleOutcomeTitle, data.ruleOutcomeTitleFr);
};

const convertAutoAssign = (data: string) => {
  return departments.value.find((department: any) => department.value === data)?.label || data;
};

const getRuleAssignment = () => {
  isLoading.value = true;
  viewRuleStore
    .getRuleAssignment()
    .then((res) => {
      ruleAssignment.value = res;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

getRuleAssignment();

const getRecordsGroup = (ruleType: string) => {
  return ruleAssignment.value.filter((rule: any) => rule.ruleAbbreviation === ruleType);
};

const manageRuleAssignment = (data: any) => {
  const ruleAssignment = getRecordsGroup(data.ruleAbbreviation);
  const dialogRef = dialog.open(ManageRuleAssignmentModal, {
    props: {
      header: t('config_items.rule_assignment'),
      modal: true,
      style: { width: 'min(980px, 84vw)' }
    },
    data: {
      ruleAssignment
    },
    emits: {
      onApply: () => {
        getRuleAssignment();
        dialogRef.close();
      }
    }
  });
};
</script>

<template>
  <div class="rule-assignment-table">
    <LoadingSpinner v-if="isLoading" />
    <div class="card">
      <h5>{{ $t('config_items.rule_assignment') }}</h5>
      <DataTable
        v-model:filters="filters"
        scrollHeight="1200px"
        scrollable
        rowGroupMode="rowspan"
        :groupRowsBy="['section', `rule${convertKey()}`, 'ruleAbbreviation']"
        :value="ruleAssignment"
        showGridlines
        filterDisplay="menu"
        :globalFilterFields="globalFilterFields"
      >
        <template #header>
          <div class="flex justify-content-between flex-col sm:flex-row">
            <div class="flex justify-content-left flex-col sm:flex-row gap-3">
              <Button id="clear-filter-btn" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="mb-2" @click="clearFilter()" />
            </div>
            <div class="justify-content-right">
              <span class="p-input-icon-left mb-2">
                <i class="pi pi-search" />
                <InputText id="search-input" v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
              </span>
            </div>
          </div>
        </template>
        <template #empty> {{ $t('management.no_data_found') }} </template>
        <TableColumn :show-filter-operator="false" :show-add-button="false" showFilterMatchModes v-for="column of columns" :key="column.field" :field="column.field">
          <template #header>
            <span>
              {{ column.header }}
            </span>
          </template>
          <template #body="{ data, field }">
            <span v-if="field === `ruleOutcomeTitle${convertKey()}`">
              <div>
                {{ convertDataRuleOutcome(data) }}
              </div>
            </span>
            <span v-else-if="field === `rule${convertKey()}`">
              <div>
                <HTMLDisplay :enHTMLData="data.rule" :frHTMLData="data.ruleFr" :currentLanguage="currentLanguage" />
              </div>
            </span>
            <span v-else-if="field === 'autoAssign'">
              <div>
                {{ convertAutoAssign(data.autoAssign) }}
              </div>
            </span>
            <span v-else-if="field === 'ruleAbbreviation'">
              <div v-if="authorizations?.configurationPage?.configurable_items_edit">
                <Button id="edit-rule-assign-btn" @click="manageRuleAssignment(data)" icon="pi pi-pencil" class="p-button-text" />
              </div>
            </span>
            <span v-else>
              {{ data[field] }}
            </span>
          </template>
          <template #filter="{ filterModel }" v-if="globalFilterFields.includes(column.field)">
            <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="column.header" />
          </template>
        </TableColumn>
      </DataTable>
    </div>
  </div>
</template>
